import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Typography, Stack, Tooltip, Backdrop } from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { SitePhoto, isVideoPhoto } from '../../../models/SitePhoto';
import { SitePhotoService } from '../../../services/SitePhotoService';

interface PhotoLightboxDialogProps {
  open: boolean;
  photos: SitePhoto[];
  initialIndex: number;
  onClose: () => void;
}

/**
 * 사진 라이트박스. 전체 사진 배열을 받아 현재 인덱스의 이미지를 큰 화면에
 * 표시하고 좌/우 화살표(또는 키보드 ←/→) 로 이동, ESC 로 닫기.
 *
 * - 다운로드 버튼 : <a download={originalName}> 트릭으로 브라우저 저장 다이얼로그.
 *   백엔드가 Content-Disposition: attachment 를 안 줘도 동작 (cross-origin 인
 *   경우엔 fallback 으로 새 탭).
 * - "원본 새 탭" 버튼도 함께 노출 — 줌·회전 등 브라우저 기본 뷰어 활용.
 *
 * Backdrop 위에 직접 마운트해 Dialog 의 paper 폭 제한을 회피한다.
 */
const PhotoLightboxDialog: React.FC<PhotoLightboxDialogProps> = ({
  open,
  photos,
  initialIndex,
  onClose,
}) => {
  const [index, setIndex] = useState(initialIndex);

  // open 또는 initialIndex 가 바뀌면 동기화
  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const goPrev = useCallback(() => {
    if (photos.length === 0) return;
    setIndex((i) => (i - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const goNext = useCallback(() => {
    if (photos.length === 0) return;
    setIndex((i) => (i + 1) % photos.length);
  }, [photos.length]);

  // 키보드 단축키
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, goPrev, goNext]);

  const current = photos[index];

  if (!open || !current) return null;

  const fileUrl = SitePhotoService.fileUrl(current.id);
  const fileName = current.original_name || current.filename || `photo-${current.id}`;

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{
        zIndex: (t) => t.zIndex.modal + 10,
        bgcolor: 'rgba(0,0,0,0.92)',
        flexDirection: 'column',
      }}
    >
      {/* 상단 툴바 */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          right: 16,
          color: 'common.white',
          zIndex: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          {fileName}
          <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            ({index + 1} / {photos.length})
          </Typography>
        </Typography>
        <Tooltip title="다운로드">
          <IconButton
            component="a"
            href={fileUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'common.white' }}
            aria-label="다운로드"
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="원본 새 탭">
          <IconButton
            component="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'common.white' }}
            aria-label="원본 새 탭"
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="닫기 (ESC)">
          <IconButton onClick={onClose} sx={{ color: 'common.white' }} aria-label="닫기">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* 좌측 이동 */}
      {photos.length > 1 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'common.white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          aria-label="이전 사진"
        >
          <PrevIcon />
        </IconButton>
      )}

      {/* 이미지 */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isVideoPhoto(current) ? (
          <video
            key={current.id} // 항목 이동 시 이전 영상이 이어 재생되지 않게 재마운트
            src={fileUrl}
            controls
            autoPlay
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              display: 'block',
            }}
          />
        ) : (
          <img
            src={fileUrl}
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              display: 'block',
              userSelect: 'none',
            }}
          />
        )}
      </Box>

      {/* 우측 이동 */}
      {photos.length > 1 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'common.white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          aria-label="다음 사진"
        >
          <NextIcon />
        </IconButton>
      )}

      {/* 하단 캡션 */}
      {(current.note || current.uploaded_by) && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
            color: 'common.white',
            textAlign: 'center',
          }}
        >
          {current.note && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {current.note}
            </Typography>
          )}
          {current.uploaded_by && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              by {current.uploaded_by}
            </Typography>
          )}
        </Box>
      )}
    </Backdrop>
  );
};

export default PhotoLightboxDialog;
